/* ─────────────────────────────────────────────────────────────────────────────
   WIT market helper (config + markup in snippets/wit-market.liquid)
   - Country / language selectors (header, mobile drawer, footer) submit through
     the one hidden #WitMarketForm, with a warning before a cross-domain switch
     that would leave the bag behind (carts don't cross domains).
   - "Right site" suggestion banner, after load + idle.
   - <wit-market-notice> in the cart drawer: decides for itself on every drawer
     render, since cart-drawer.js swaps the drawer's innerHTML on each change.
   QA: ?wit_geo=FR fakes the detected country, ?wit_lang=de the browser
   language, ?wit_mkt_reset=1 clears every stored choice.
   ───────────────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const cfgEl = document.getElementById('WitMarketConfig');
  if (!cfgEl) return;
  let cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (_) {
    return;
  }
  const str = cfg.strings || {};
  const countries = cfg.countries || {};
  const languages = cfg.languages || {};

  /* ─── Storage (every access guarded: private mode / blocked storage throw) ─── */
  function store(kind) {
    return {
      get(key) {
        try { return window[kind].getItem(key); } catch (_) { return null; }
      },
      set(key, value) {
        try { window[kind].setItem(key, value); return true; } catch (_) { return false; }
      },
      remove(key) {
        try { window[kind].removeItem(key); } catch (_) {}
      },
      clearPrefix(prefix) {
        try {
          const s = window[kind];
          for (let i = s.length - 1; i >= 0; i--) {
            const k = s.key(i);
            if (k && k.indexOf(prefix) === 0) s.removeItem(k);
          }
        } catch (_) {}
      },
    };
  }
  const ls = store('localStorage');
  const ss = store('sessionStorage');

  /* ─── Cookie shared by both domains (Domain=wit-training.com covers eu.) ─── */
  const COOKIE = 'wit_mkt';
  function cookieDomain() {
    const host = location.hostname;
    const root = cfg.rootDomain;
    return root && (host === root || host.endsWith('.' + root)) ? root : '';
  }
  function writeCookie(value, maxAge) {
    try {
      let c = `${COOKIE}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax; Secure`;
      const domain = cookieDomain();
      if (domain) c += `; Domain=${domain}`;
      document.cookie = c;
    } catch (_) {}
  }
  function readCookie() {
    try {
      const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE}=([^;]*)`));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (_) {
      return null;
    }
  }

  const storageOK = (() => {
    if (ls.set('wit:test', '1')) {
      ls.remove('wit:test');
      return true;
    }
    try {
      document.cookie = 'wit_test=1; Path=/; SameSite=Lax; Secure';
      const ok = document.cookie.indexOf('wit_test=1') > -1;
      document.cookie = 'wit_test=; Max-Age=0; Path=/; SameSite=Lax; Secure';
      return ok;
    } catch (_) {
      return false;
    }
  })();

  const params = (() => {
    try { return new URLSearchParams(location.search); } catch (_) { return null; }
  })();
  const param = (name) => (params ? params.get(name) : null);

  /* ?wit_mkt_reset=1 — forget every stored choice (QA) */
  if (param('wit_mkt_reset')) {
    writeCookie('', 0);
    try { document.cookie = `${COOKIE}=; Max-Age=0; Path=/`; } catch (_) {}
    ls.clearPrefix('wit:');
    ss.clearPrefix('wit:');
  }

  const days = () => Math.max(1, parseInt(cfg.dismissDays, 10) || 30);

  /* "stay" (banner/notice) or "chosen" (manual selector change). Both silence
     the banner and the bag notice on both domains for dismissDays. The in-memory
     flag covers visitors with no storage for the rest of this page. */
  function suppress(kind) {
    window.__witMktStay = true;
    writeCookie(kind, days() * 86400);
    ls.set('wit:mkt', JSON.stringify({ v: kind, exp: Date.now() + days() * 864e5 }));
  }
  function unexpired(key) {
    try {
      const o = JSON.parse(ls.get(key) || 'null');
      return !!(o && o.exp > Date.now());
    } catch (_) {
      return false;
    }
  }
  function suppressed() {
    return !!window.__witMktStay || !!readCookie() || unexpired('wit:mkt');
  }

  /* ─── Domains, countries, languages ─── */
  const euCountries = new Set(
    String(cfg.euCountries || '')
      .toUpperCase()
      .split(/[^A-Z]+/)
      .filter((c) => c.length === 2)
  );
  const siteFor = (cc) => (euCountries.has(String(cc || '').toUpperCase()) ? 'eu' : 'root');
  const siteName = (site) => (site === 'eu' ? str.siteEu : str.siteRoot);

  function euLanguages() {
    const list = cfg.site === 'eu' ? Object.keys(languages) : String(cfg.euLanguages || '').split(/[\s,]+/);
    return list.filter(Boolean).map((l) => l.toLowerCase());
  }
  function match(code) {
    const base = String(code || '').toLowerCase().split('-')[0];
    return euLanguages().indexOf(base) > -1 ? base : null;
  }
  /* First browser language the EU domain has, or null */
  function navLanguage() {
    const list = (navigator.languages && navigator.languages.length && navigator.languages) || [navigator.language];
    for (let i = 0; i < list.length; i++) {
      const hit = match(list[i]);
      if (hit) return hit;
    }
    return null;
  }
  const langOverride = () => match(param('wit_lang'));

  /* Theme setting "FR:fr,BE:nl|fr,…" → { FR: ['fr'], BE: ['nl', 'fr'] } */
  const countryLanguages = (() => {
    const map = {};
    String(cfg.countryLanguages || '')
      .split(/[,;\n]+/)
      .forEach((entry) => {
        const parts = entry.split(':');
        if (parts.length !== 2) return;
        const cc = parts[0].trim().toUpperCase();
        const langs = parts[1]
          .split('|')
          .map((l) => l.trim().toLowerCase())
          .filter(Boolean);
        if (/^[A-Z]{2}$/.test(cc) && langs.length) map[cc] = langs;
      });
    return map;
  })();
  /* Browser language base codes in order, ?wit_lang=xx (QA) first */
  function browserLanguages() {
    const nav = (navigator.languages && navigator.languages.length && navigator.languages) || [navigator.language];
    return [param('wit_lang')]
      .concat(Array.from(nav))
      .map((l) => String(l || '').toLowerCase().split('-')[0])
      .filter(Boolean);
  }
  /* Default eu. language for a country: the first browser language the
     country lists, else the country's first language; only languages eu. has.
     Unlisted country (or none available) → 'en'. */
  function countryLanguage(cc) {
    const available = euLanguages();
    const langs = (countryLanguages[String(cc || '').toUpperCase()] || []).filter((l) => available.indexOf(l) > -1);
    if (!langs.length) return 'en';
    const browser = browserLanguages();
    for (let i = 0; i < browser.length; i++) {
      if (langs.indexOf(browser[i]) > -1) return browser[i];
    }
    return langs[0];
  }

  /* Locale root with a trailing slash: '/' or '/fr/' (routes.root_url is '/fr') */
  function routesRoot() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || cfg.routesRoot || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  /* Current path without the locale prefix (/fr/products/x → /products/x): a
     /de/ path 404s on wit-training.com, so return_to must be prefix-free. */
  function cleanPath() {
    let path = location.pathname || '/';
    const root = routesRoot();
    if (root !== '/') {
      const base = root.replace(/\/$/, '').toLowerCase();
      const lower = path.toLowerCase();
      if (lower === base || lower === base + '/') path = '/';
      else if (lower.indexOf(base + '/') === 0) path = path.slice(base.length);
    } else {
      const codes = euLanguages()
        .concat('en')
        .map((c) => c.replace(/[^a-z-]/g, ''))
        .filter(Boolean)
        .join('|');
      path = path.replace(new RegExp(`^/(?:${codes}|[a-z]{2}-[a-z]{2})(?=/|$)`, 'i'), '') || '/';
    }
    const query = new URLSearchParams(location.search);
    ['wit_geo', 'wit_lang', 'wit_mkt_reset'].forEach((k) => query.delete(k));
    const q = query.toString();
    return path + (q ? `?${q}` : '');
  }

  /* Liquid HTML-escapes strings (& → &amp;, ' → &#39;): the t filter and
     country.name alike. Decode once with an inert parser. */
  function decode(s) {
    let out = String(s == null ? '' : s);
    if (/&[#a-z0-9]+;/i.test(out)) {
      try { out = new DOMParser().parseFromString(out, 'text/html').documentElement.textContent; } catch (_) {}
    }
    return out;
  }
  /* Decode the template and each value separately (so a decoded value is never
     decoded twice), then fill the [tokens]. Always written with textContent. */
  function text(s, values) {
    return decode(s).replace(/\[(country|currency|site|language)\]/g, (m, k) =>
      values && values[k] != null ? decode(values[k]) : m
    );
  }

  /* ─── Submit through the one localization form ─── */
  function setField(form, name, value) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /* language: undefined → picked by target site (root: en; eu: the country's
     default, see countryLanguage). A language the visitor chose themselves wins
     on eu. exact: send as given (header language buttons, banner offer). */
  function submit({ country, language, exact }) {
    const target = siteFor(country);
    let lang = language;
    if (!exact) {
      if (target === 'root') lang = 'en';
      else lang = language || countryLanguage(country);
    }

    let form = document.getElementById('WitMarketForm');
    if (!form) {
      form = document.createElement('form');
      form.method = 'post';
      form.action = '/localization';
      form.hidden = true;
      setField(form, 'form_type', 'localization');
      setField(form, 'utf8', '✓');
      document.body.appendChild(form);
    }
    setField(form, 'country_code', country);
    setField(form, 'language_code', lang || cfg.currentLanguage || 'en');
    // Shopify ignores return_to "/" and redirects to the Referer instead (so
    // ?wit_geo / ?wit_mkt_reset would ride along). "/." is not ignored, and
    // browsers resolve it to "/" (URL dot-segment rule).
    const returnTo = cleanPath();
    setField(form, 'return_to', returnTo === '/' ? '/.' : returnTo);
    setField(form, '_method', 'put');
    form.submit();
  }

  /* ─── Live bag count (header count is unreliable) ─── */
  async function cartCount() {
    const ctrl = 'AbortController' in window ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 2000) : 0;
    try {
      const root = routesRoot();
      const res = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
      });
      const cart = await res.json();
      return parseInt(cart.item_count, 10) || 0;
    } catch (_) {
      return parseInt(cfg.cartCount, 10) || 0;
    } finally {
      clearTimeout(timer);
    }
  }

  /* ─── Country detection: one request per session, cached ('?' = failed) ─── */
  const geoParam = () => {
    const g = String(param('wit_geo') || '').toUpperCase();
    return /^[A-Z]{2}$/.test(g) ? g : null;
  };
  const valid = (cc) => (cc && cc !== '?' && countries[cc] ? cc : null);

  let detecting = null;
  function detect() {
    if (detecting) return detecting;
    detecting = (async () => {
      let cc = geoParam();
      if (cc) {
        ss.set('wit:geo', cc);
        return valid(cc);
      }
      cc = ss.get('wit:geo');
      if (cc) return valid(cc);

      const ctrl = 'AbortController' in window ? new AbortController() : null;
      const timer = ctrl ? setTimeout(() => ctrl.abort(), 3000) : 0;
      try {
        const res = await fetch('/browsing_context_suggestions.json', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: ctrl ? ctrl.signal : undefined,
        });
        const data = await res.json();
        const handle = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
        cc = handle ? String(handle).toUpperCase() : '?';
      } catch (_) {
        cc = '?';
      } finally {
        clearTimeout(timer);
      }
      ss.set('wit:geo', cc);
      return valid(cc);
    })();
    return detecting;
  }

  function isBot() {
    try {
      if (navigator.webdriver) return true;
      if (document.visibilityState === 'prerender') return true;
      return /bot|crawl|spider|slurp|bingpreview|lighthouse|headless|pagespeed|gtmetrix|facebookexternalhit|embedly|preview/i.test(
        navigator.userAgent || ''
      );
    } catch (_) {
      return true;
    }
  }

  function whenIdle(fn) {
    const run = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 4000 });
      else setTimeout(fn, 2000);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  function publish(action, cc) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('wit_market_banner', { action, cc: cc || null });
      }
    } catch (_) {}
  }

  /* ─── Selectors (header buttons, drawer + footer forms) ─── */
  function closeHeaderPanel(origin) {
    const root = origin && origin.closest('[data-wit-loc]');
    if (!root) return;
    const panel = root.querySelector('[data-wit-loc-panel]');
    const toggle = root.querySelector('.wit-header__localization-toggle');
    if (panel) panel.hidden = true;
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    }
  }

  /* Inline "your bag stays here" warning in the nearest confirm slot */
  function confirmSwitch(origin, target) {
    const holder = origin && origin.closest('[data-wit-loc], [data-wit-loc-form]');
    const slot = holder && holder.querySelector('[data-wit-loc-confirm]');
    const body = text(str.warnBody, { site: siteName(target) });
    if (!slot) return Promise.resolve(window.confirm(body));

    slot.textContent = '';
    const p = document.createElement('p');
    p.className = 'wit-loc__confirm-text';
    p.textContent = body;
    const actions = document.createElement('div');
    actions.className = 'wit-loc__confirm-actions';
    const yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'wit-loc__confirm-btn wit-loc__confirm-btn--primary';
    yes.textContent = text(str.warnConfirm, { site: siteName(target) });
    const no = document.createElement('button');
    no.type = 'button';
    no.className = 'wit-loc__confirm-btn';
    no.textContent = text(str.warnCancel);
    actions.append(yes, no);
    slot.append(p, actions);
    slot.hidden = false;
    yes.focus();

    return new Promise((resolve) => {
      yes.addEventListener('click', () => resolve(true), { once: true });
      no.addEventListener(
        'click',
        () => {
          // Hide now, empty after this click has bubbled: removing the button
          // mid-click detaches e.target, and the header's outside-click check
          // would then close the whole panel.
          slot.hidden = true;
          setTimeout(() => {
            if (slot.hidden) slot.textContent = '';
          }, 0);
          if (origin && origin.focus) origin.focus();
          resolve(false);
        },
        { once: true }
      );
    });
  }

  async function change(cc, lang, origin) {
    if (!cc || !countries[cc]) return;
    const target = siteFor(cc);
    // Same country (and same currency — root's localization.country can read GB
    // while prices show in USD) and same language: nothing to do.
    const sameCountry = cc === cfg.currentCountry && countries[cc][1] === cfg.currentCurrency;
    const sameLanguage = !lang || lang === cfg.currentLanguage;
    if (sameCountry && sameLanguage) {
      closeHeaderPanel(origin);
      return;
    }
    if (cfg.site !== 'other' && target !== cfg.site && (await cartCount()) > 0) {
      if (!(await confirmSwitch(origin, target))) {
        // Drawer / footer: put the selects back on the current country
        const form = origin && origin.closest('form[data-wit-loc-form]');
        if (form) {
          form.reset();
          delete form.dataset.witLangDirty;
        }
        return;
      }
    }
    suppress('chosen');
    // A language the visitor picked on this domain only applies when staying
    // on it; otherwise the country decides (countryLanguage).
    submit({ country: cc, language: target === cfg.site && lang ? lang : undefined });
  }

  document.addEventListener('click', (e) => {
    const langBtn = e.target.closest && e.target.closest('[data-wit-loc-language]');
    if (langBtn) {
      e.preventDefault();
      const lang = langBtn.getAttribute('data-wit-loc-language');
      if (lang === cfg.currentLanguage) {
        closeHeaderPanel(langBtn);
        return;
      }
      suppress('chosen');
      submit({ country: cfg.currentCountry, language: lang, exact: true });
      return;
    }
    const countryBtn = e.target.closest && e.target.closest('[data-wit-loc-country]');
    if (countryBtn) {
      e.preventDefault();
      change(countryBtn.getAttribute('data-wit-loc-country'), null, countryBtn);
    }
  });

  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[data-wit-loc-form]')) return;
    e.preventDefault();
    const country = form.elements.country_code ? form.elements.country_code.value : null;
    const langField = form.elements.language_code;
    // Only a language the visitor changed themselves counts as a choice
    const lang = langField && langField.tagName === 'SELECT' && form.dataset.witLangDirty ? langField.value : null;
    change(country, lang, form.querySelector('.wit-loc-form__submit') || form);
  });

  document.addEventListener('change', (e) => {
    const field = e.target;
    if (!field || field.tagName !== 'SELECT' || field.name !== 'language_code') return;
    const form = field.closest('form[data-wit-loc-form]');
    if (form) form.dataset.witLangDirty = '1';
  });

  /* ─── Suggestion banner ─── */
  /* Never stack on Shopify's cookie banner or an open modal (Klaviyo popup,
     cart drawer, quick add…). offsetParent is always null for position:fixed,
     so check boxes + computed style instead. */
  const MODALS = '[aria-modal="true"], [role="dialog"], [role="alertdialog"], [data-testid="POPUP"], [aria-label="POPUP Form"]';
  function shown(el) {
    try {
      if (!el || !el.isConnected || !el.getClientRects().length) return false;
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return false;
      // Closed drawers sit translated just off-screen while still "visible"
      if (r.right <= 1 || r.bottom <= 1 || r.left >= window.innerWidth - 1 || r.top >= window.innerHeight - 1) return false;
      if (getComputedStyle(el).visibility === 'hidden') return false;
      for (let n = el; n && n.nodeType === 1; n = n.parentElement) {
        const cs = getComputedStyle(n);
        if (cs.display === 'none' || parseFloat(cs.opacity) === 0) return false;
      }
      return true;
    } catch (_) {
      return false;
    }
  }
  let consentDone = false;
  document.addEventListener('visitorConsentCollected', () => { consentDone = true; });
  /* shouldShowBanner() can be true (consent required in the visitor's region)
     while no cookie banner is ever rendered, so only wait for it briefly. */
  const COOKIE_GRACE_MS = 5000;
  const pageStart = Date.now();
  function cookieBannerOpen() {
    if (shown(document.getElementById('shopify-pc__banner'))) return true;
    if (consentDone || Date.now() - pageStart > COOKIE_GRACE_MS) return false;
    try {
      const cp = window.Shopify && Shopify.customerPrivacy;
      return !!(cp && typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner());
    } catch (_) {
      return false;
    }
  }
  function modalOpen() {
    const list = document.querySelectorAll(MODALS);
    for (let i = 0; i < list.length; i++) {
      if (!list[i].closest('.wit-market-banner') && shown(list[i])) return true;
    }
    return false;
  }
  const blocked = () => cookieBannerOpen() || modalOpen();
  /* Resolves true once nothing is in the way, false after maxMs (skip this page) */
  function whenClear(maxMs) {
    return new Promise((resolve) => {
      if (!blocked()) return resolve(true);
      const start = Date.now();
      const tick = () => {
        if (!blocked()) resolve(true);
        else if (Date.now() - start > maxMs) resolve(false);
        else setTimeout(tick, 500);
      };
      setTimeout(tick, 500);
    });
  }
  function removeBanners() {
    document.querySelectorAll('.wit-market-banner').forEach((b) => b.parentNode && b.parentNode.removeChild(b));
  }

  async function banner() {
    const forced = !!geoParam();
    if (!cfg.bannerEnabled || cfg.designMode || cfg.site === 'other') return;
    if (!storageOK || suppressed()) return;
    if (!forced && isBot()) return;
    const views = parseInt(ss.get('wit:mkt-views'), 10) || 0;
    if (views >= 3) return;
    const tpl = document.getElementById('WitMarketBanner');
    if (!tpl || !tpl.content) return;

    let offer = null;
    const cc = await detect();
    if (cc && siteFor(cc) !== cfg.site) {
      const target = siteFor(cc);
      offer = {
        cc,
        text: text(str.countryPrompt, { country: countries[cc][0] }),
        cta: text(str.countryCta, { site: siteName(target), currency: countries[cc][1] }),
        stay: text(str.stay),
        note: (await cartCount()) > 0 ? text(str.bagNote) : '',
        go: () => {
          suppress('stay');
          submit({ country: cc });
        },
        dismiss: () => suppress('stay'),
      };
    } else if (cfg.site === 'eu' && routesRoot() === '/' && !unexpired('wit:mkt-lang')) {
      // Only on eu.'s default-language (unprefixed) pages: anyone on /fr, /de…
      // chose that language or was linked to it.
      const lang = langOverride() || navLanguage();
      const remember = () => ls.set('wit:mkt-lang', JSON.stringify({ exp: Date.now() + days() * 864e5 }));
      if (lang && lang !== cfg.currentLanguage && languages[lang]) {
        offer = {
          cc,
          lang,
          text: text(str.languagePrompt, { language: languages[lang] }),
          cta: text(str.languageCta, { language: languages[lang] }),
          stay: text(str.languageStay, { language: languages[cfg.currentLanguage] || cfg.currentLanguage }),
          note: '',
          go: () => {
            remember();
            submit({ country: cfg.currentCountry, language: lang, exact: true });
          },
          dismiss: remember,
        };
      }
    }
    if (!offer) return;

    if (!(await whenClear(60000))) return;
    if (suppressed() || document.querySelector('.wit-market-banner')) return;
    ss.set('wit:mkt-views', String(views + 1));

    const frag = tpl.content.cloneNode(true);
    const el = frag.querySelector('.wit-market-banner');
    if (!el) return;
    el.querySelector('[data-text]').textContent = offer.text;
    const note = el.querySelector('[data-bag-note]');
    if (note && offer.note) {
      note.textContent = offer.note;
      note.hidden = false;
    }
    const cta = el.querySelector('[data-act="switch"]');
    cta.querySelector('[data-label]').textContent = offer.cta;
    if (offer.lang) cta.setAttribute('lang', offer.lang);
    const stay = el.querySelector('[data-act="stay"]');
    const stayLabel = stay.querySelector('[data-label]');
    (stayLabel || stay).textContent = offer.stay;

    let watch = 0;
    const remove = () => {
      clearInterval(watch);
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    el.addEventListener('click', (e) => {
      const act = e.target.closest('[data-act]');
      if (!act) return;
      if (act.getAttribute('data-act') === 'switch') {
        publish('switch', offer.cc);
        offer.go();
      } else {
        publish('stay', offer.cc);
        offer.dismiss();
        remove();
      }
    });
    // Escape only while focus is in the banner, so closing the search or menu
    // with Escape doesn't also dismiss it for 30 days.
    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      publish('stay', offer.cc);
      offer.dismiss();
      remove();
    });

    document.body.appendChild(frag);
    publish('shown', offer.cc);
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-in')));
    // Step aside while a popup / drawer / cookie banner opens over the page
    watch = setInterval(() => {
      if (!el.isConnected) return clearInterval(watch);
      el.hidden = blocked();
    }, 1000);
  }

  whenIdle(() => {
    banner().catch(() => {});
  });

  /* ─── Bag notice in the cart drawer ─── */
  class WitMarketNotice extends HTMLElement {
    connectedCallback() {
      if (!cfg.noticeEnabled || cfg.site === 'other' || suppressed()) return;
      const forced = geoParam();
      const cached = forced || ss.get('wit:geo');
      if (cached) {
        this.decide(valid(cached));
        return;
      }
      if (isBot()) return;
      whenIdle(() => detect().then((cc) => this.decide(cc), () => {}));
    }

    decide(cc) {
      if (!cc || !this.isConnected || suppressed()) return;
      const target = siteFor(cc);
      if (target === cfg.site) return;
      const site = siteName(target);
      const set = (sel, value) => {
        const node = this.querySelector(sel);
        if (node) node.textContent = value;
      };
      set('[data-heading]', text(str.noticeHeading, { country: countries[cc][0] }));
      set('[data-body]', text(str.noticeBody, { site, currency: countries[cc][1] }));
      set('[data-act="switch"]', `${text(str.noticeCta, { site })} →`);
      set('[data-act="stay"]', text(str.noticeStay));
      this.hidden = false;
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (e) => {
        const act = e.target.closest('[data-act]');
        if (!act) return;
        if (act.getAttribute('data-act') === 'switch') {
          submit({ country: cc });
        } else {
          suppress('stay');
          this.hidden = true;
          removeBanners();
        }
      });
    }
  }

  if (!customElements.get('wit-market-notice')) {
    customElements.define('wit-market-notice', WitMarketNotice);
  }
})();
